<template>
    <div class="login-container">
      <el-card class="login-card">
        <div class="logo-container">
          <img src="../assets/DiCloudAccess.svg" class="logo">
        </div>
        <h2 class="login-title">点云通软件管理系统</h2>
        <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef" label-position="top">
          <el-form-item prop="username">
            <el-input v-model="loginForm.username" placeholder="用户名">
              <template #prefix>
                <el-icon><User /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item prop="password">
            <el-input v-model="loginForm.password" :type="passwordVisible ? 'text' : 'password'" placeholder="密码" @keyup.enter="handleLogin">
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
              <template #suffix>
                <el-icon class="password-eye" @click="passwordVisible = !passwordVisible">
                  <View v-if="passwordVisible" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleLogin" :loading="loading" class="login-button">登录</el-button>
          </el-form-item>
        </el-form>
      </el-card>
      
      <el-dialog
        v-model="showChangePasswordDialog"
        title="首次登录修改密码"
        width="400px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false">
        <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-position="top">
          <el-form-item label="新密码" prop="newPassword">
            <el-input v-model="passwordForm.newPassword" :type="newPasswordVisible ? 'text' : 'password'" placeholder="请输入新密码">
              <template #suffix>
                <el-icon class="password-eye" @click="newPasswordVisible = !newPasswordVisible">
                  <View v-if="newPasswordVisible" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input v-model="passwordForm.confirmPassword" :type="confirmPasswordVisible ? 'text' : 'password'" placeholder="请再次输入新密码">
              <template #suffix>
                <el-icon class="password-eye" @click="confirmPasswordVisible = !confirmPasswordVisible">
                  <View v-if="confirmPasswordVisible" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="changePassword" :loading="changingPassword" style="width: 100%;">确认修改</el-button>
          </el-form-item>
        </el-form>
      </el-dialog>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { ElMessage } from 'element-plus'
  import { User, Lock, View, Hide } from '@element-plus/icons-vue'
  import { useHead } from '@vueuse/head'
  import authApi from '../js/connect.js'
  
  useHead({
    title: '登录',
  })
  
  const router = useRouter()
  const loginFormRef = ref(null)
  const passwordFormRef = ref(null)
  const loading = ref(false)
  const changingPassword = ref(false)
  const showChangePasswordDialog = ref(false)
  const passwordVisible = ref(false)
  const newPasswordVisible = ref(false)
  const confirmPasswordVisible = ref(false)
  
  const loginForm = reactive({
    username: '',
    password: ''
  })
  
  const passwordForm = reactive({
    newPassword: '',
    confirmPassword: ''
  })
  
  const loginRules = {
    username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
    password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
  }
  
  const passwordRules = {
    newPassword: [
      { required: true, message: '请输入新密码', trigger: 'blur' },
      { min: 8, message: '密码长度至少为8个字符', trigger: 'blur' }
    ],
    confirmPassword: [
      { required: true, message: '请再次输入新密码', trigger: 'blur' },
      { 
        validator: (rule, value, callback) => {
          if (value !== passwordForm.newPassword) {
            callback(new Error('两次输入的密码不一致'));
          } else {
            callback();
          }
        }, 
        trigger: 'blur' 
      }
    ]
  }
  
  // 解析JWT token并检查是否过期
  const isTokenValid = (token) => {
    if (!token) {
      return false;
    }
    
    try {
      // JWT token格式: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      // 解码payload（base64解码）
      const payload = JSON.parse(atob(parts[1]));
      
      // 检查exp字段（过期时间，Unix时间戳）
      if (payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        // 如果当前时间大于过期时间，token已过期
        if (currentTime >= payload.exp) {
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Token解析失败:', error);
      return false;
    }
  }

  onMounted(() => {
    // 设置请求拦截器
    authApi.setupInterceptors(router);
    
    // 检查是否已登录且token有效
    const token = localStorage.getItem('access_token');
    if (token && isTokenValid(token)) {
      router.push('/welcome');
    } else if (token && !isTokenValid(token)) {
      // token已过期，清除登录状态
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_info');
      localStorage.removeItem('isLogin');
    }
  })
  
  const handleLogin = async () => {
    if (!loginFormRef.value) return
    
    await loginFormRef.value.validate(async (valid) => {
      if (valid) {
        loading.value = true
        try {
          const response = await authApi.adminLogin(loginForm.username, loginForm.password);
          
          // 保存令牌和用户信息
          localStorage.setItem('access_token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);
          localStorage.setItem('user_info', JSON.stringify({
            username: response.data.username,
            role: response.data.role
          }));
          localStorage.setItem('isLogin', 'true');
          
          ElMessage.success('登录成功');
          
          // 如果是第一次登录且使用默认密码，弹出修改密码对话框
          if (loginForm.username === 'admin' && loginForm.password === 'admin123') {
            showChangePasswordDialog.value = true;
          } else {
            router.push('/welcome');
          }
        } catch (error) {
          let errorMsg = '登录失败';
          if (error.response && error.response.data && error.response.data.message) {
            errorMsg = error.response.data.message;
          }
          ElMessage.error(errorMsg);
        } finally {
          loading.value = false;
        }
      } else {
        ElMessage.error('请填写所有必填字段');
      }
    });
  }
  
  const changePassword = async () => {
    if (!passwordFormRef.value) return;
    
    await passwordFormRef.value.validate(async (valid) => {
      if (valid) {
        changingPassword.value = true;
        try {
          await authApi.changePassword(loginForm.password, passwordForm.newPassword);
          ElMessage.success('密码修改成功');
          showChangePasswordDialog.value = false;
          router.push('/welcome');
        } catch (error) {
          let errorMsg = '密码修改失败';
          if (error.response && error.response.data && error.response.data.message) {
            errorMsg = error.response.data.message;
          }
          ElMessage.error(errorMsg);
        } finally {
          changingPassword.value = false;
        }
      }
    });
  }
  </script>
  
  <style scoped>
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background-color: var(--dca-bg);
  }
  
  .login-card {
    width: 350px;
    padding: 20px;
  }
  
  .logo-container {
    text-align: center;
    margin-bottom: 20px;
  }
  
  .logo {
    width: 80px;
    height: auto;
  }
  
  .login-title {
    text-align: center;
    margin-bottom: 30px;
    color: var(--dca-text);
    font-size: 24px;
  }
  
  .login-button {
    width: 100%;
  }
  
  .el-input {
    margin-bottom: 20px;
  }
  
  .password-eye {
    cursor: pointer;
  }
  </style>